import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { HrPrintService, hrPrintErrorMessage } from '../hr-print.service';
import { LoginService } from '@app/common/login.service';
import { CommonService } from '@app/common/common.service';
import { AlertService } from '@app/shared/alert.service';
import { ClockEditDialogComponent, ClockEditDialogData, ClockEditDialogResult } from './clock-edit-dialog/clock-edit-dialog.component';
import { DayValidationDialogComponent, DayValidationDialogData } from './day-validation-dialog/day-validation-dialog.component';
import { CountersignDialogComponent, CountersignDialogData, CountersignRow } from './countersign-dialog/countersign-dialog.component';
import { HrAbsence, HrClock, HrDayValidation, HrDocument, HrEmployee, hrEmployeeNameOf, hrIsHrManager } from '@app/shared/_models/hr';
import { Store, User } from '@app/shared/_models/common';
import {
	C_VALUES_HR_ABSENCE_TYPE, EVENTTYP_HR_CLOCK_CANCEL, EVENTTYP_INSERT, HR_ABSENCE_STATE_APPROVED,
	HR_CLOCK_SOURCE_MANUAL, OBJECTTYP_HR_CLOCK, OBJECTTYP_HR_DAY_VALIDATION,
} from '@app/shared/constants';
import {
	EffectiveSlot, addDays, deltaLabel, effectiveSlotsForRange, frenchHolidaysOf, hoursLabel, isoWeekOf, mondayOf,
} from '../hr-schedule-engine';
import { HrTimeSource } from '../hr-payroll';
import { countsHours, employeeWorkProfileOf } from '../hr-worktime';
import { ClockAnomaly, TimesheetRow, UnclosedDay, WorkPeriod, buildTimesheet, buildTimesheetCsv, pairClocks, unclosedDays } from '../hr-timesheet';
import { buildWorktimeLedgerDoc } from '../hr-timesheet-print';
import { DayValidationRow } from '../hr-validation';
import { todayStr, wallMinutesOf } from '@app/shared/time';

interface TimesheetVM extends TimesheetRow {
	name: string;
	absence_days: number;		// jours d'absence approuvés dans la période (calendaires, bornés)
	unclosed: UnclosedDay[];	// journées passées jamais closes (mode planning — cf. hr-timesheet.ts)
}

// Compteurs d'heures — /hr/timesheets. Badgé vs planifié vs contrat, heures sup par semaine,
// anomalies de pointage, journées non closes, corrections manuelles (admin), export CSV pour la
// paie et RELEVÉ INDIVIDUEL imprimable (art. D3171-8).
@Component({
	selector: 'app-hr-timesheets',
	standalone: true,
	imports: [MenuCommonComponent, MatTableModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatTooltipModule],
	templateUrl: './timesheets.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './timesheets.component.scss',
})
export class TimesheetsComponent implements OnInit {

	public hrService = inject(HrService);
	private printService = inject(HrPrintService);
	private loginService = inject(LoginService);
	private commonService = inject(CommonService);
	private alertService = inject(AlertService);
	private dialog = inject(MatDialog);

	public mode: 'week' | 'month' = 'week';
	public anchor: string = todayStr();
	public from = '';
	public to = '';
	public periodLabel = '';
	public isAdmin = false;
	// Mode de décompte de l'établissement : décide ce que le dialog de validation PROPOSE.
	public source: HrTimeSource = 'planning';

	// La colonne « À clore » n'existe qu'en mode planning : en mode pointeuse le badge est
	// l'enregistrement, et une colonne perpétuellement vide serait du bruit (cf. load()).
	public displayedColumns: string[] = ['name', 'days', 'worked', 'planned', 'delta', 'overtime', 'absences', 'anomalies', 'actions'];
	public dataSource: MatTableDataSource<TimesheetVM> = new MatTableDataSource<TimesheetVM>();

	private employees: HrEmployee[] = [];
	private users: User[] = [];
	private stores: Store[] = [];
	private clocks: HrClock[] = [];
	// L8 — matière du dialog de validation journalière (prévu / badgé / rémunéré).
	private validations: HrDayValidation[] = [];
	private plannedSlots: EffectiveSlot[] = [];
	private workPeriods: WorkPeriod[] = [];
	// Les anomalies du même appariement : le relevé imprimé doit signaler une sortie orpheline
	// (aucune WorkPeriod ne la porte), comme la colonne « Anomalies » de l'écran.
	private clockAnomalies: ClockAnomaly[] = [];
	// Libellés des types d'absence (c_values 23) — le relevé imprimé écrit « Congés payés »,
	// pas un numéro de type.
	private absenceNames = new Map<number, string>();
	// Relevés signés par un salarié, EN ATTENTE du visa du responsable (events 4401/4402).
	public pendingCountersign: CountersignRow[] = [];

	ngOnInit(): void {
		this.load();
	}

	// ============================================================
	// Période affichée
	// ============================================================

	private computeRange(): void {
		if (this.mode === 'week') {
			this.from = mondayOf(this.anchor);
			this.to = addDays(this.from, 6);
			this.periodLabel = 'S' + isoWeekOf(this.from) + ' — du ' + this.fmt(this.from) + ' au ' + this.fmt(this.to);
		} else {
			this.from = this.anchor.slice(0, 8) + '01';
			const nextMonth = addDays(this.from, 32).slice(0, 8) + '01';
			this.to = addDays(nextMonth, -1);
			this.periodLabel = this.from.slice(5, 7) + '/' + this.from.slice(0, 4);
		}
	}

	private fmt(dat: string): string {
		return dat.slice(8, 10) + '/' + dat.slice(5, 7);
	}

	onModeChange(mode: 'week' | 'month'): void {
		this.mode = mode;
		this.load();
	}

	onShift(delta: number): void {
		// Mois : ancrer sur le 1er du mois voisin (± 31 jours sautait un mois court, ex. juillet → mai).
		this.anchor = this.mode === 'week'
			? addDays(mondayOf(this.anchor), delta * 7)
			: (delta > 0 ? addDays(this.to, 1) : addDays(this.from, -1));
		this.load();
	}

	onToday(): void {
		this.anchor = todayStr();
		this.load();
	}

	// ============================================================
	// Chargement + calcul
	// ============================================================

	private load(): void {
		this.computeRange();
		forkJoin({
			employees: this.hrService.getEmployees$(),
			users: this.hrService.getUsers$(),
			schedules: this.hrService.getSchedules$(),
			overrides: this.hrService.getOverrides$(this.from, this.to),
			absences: this.hrService.getAbsences$(this.from, this.to, HR_ABSENCE_STATE_APPROVED),
			clocks: this.hrService.getClocks$(this.from, this.to),
			validations: this.hrService.getValidations$(this.from, this.to),
			payrollRules: this.hrService.payrollRules$(),
			// Pour le RELEVÉ IMPRIMÉ : le nom de l'établissement et les libellés d'absence. Leurs
			// pannes ne doivent pas priver l'écran de ses compteurs — repli sur des listes vides.
			stores: this.commonService.getStores().pipe(catchError(() => of([] as Store[]))),
			absenceTypes: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			// Les relevés signés (events 4401/4402) vivent dans les documents des fiches : c'est
			// là que se comptent ceux qui attendent le visa du responsable.
			documents: this.hrService.getDocuments$().pipe(catchError(() => of([] as HrDocument[]))),
		}).subscribe(({ employees, users, schedules, overrides, absences, clocks, validations, payrollRules, stores, absenceTypes, documents }) => {
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);		// la base fait foi (vieux JWT sans roles)
			this.employees = employees;
			this.users = users;
			this.stores = stores;
			this.clocks = clocks;
			this.validations = validations;
			this.source = payrollRules.rules.time_source;
			this.absenceNames = new Map<number, string>(absenceTypes
				.filter(t => t.id != null)
				.map(t => [t.id as number, t.name ?? 'Absence']));
			this.displayedColumns = this.source === 'clock'
				? ['name', 'days', 'worked', 'planned', 'delta', 'overtime', 'absences', 'anomalies', 'actions']
				: ['name', 'days', 'worked', 'planned', 'delta', 'overtime', 'absences', 'anomalies', 'unclosed', 'actions'];

			// Les relevés signés en attente de visa — tous salariés, toutes semaines (un retard de
			// visa ne doit pas disparaître avec le changement de période affichée).
			this.pendingCountersign = documents
				.map(d => ({ d, s: HrDocument.timesheetSignOf(d) }))
				.filter(x => x.s && !x.s.manager)
				.map(x => ({
					doc: x.d,
					name: hrEmployeeNameOf(employees.find(e => e.id === x.d.id_employee), users),
					week: x.s!.week,
					total_min: x.s!.total_min ?? 0,
					signed_at: x.s!.employee?.at ?? '',
				}))
				.sort((a, b) => a.week.localeCompare(b.week) || a.name.localeCompare(b.name));

			const planned = effectiveSlotsForRange(this.from, this.to, schedules, overrides, absences);
			const { periods, anomalies } = pairClocks(clocks, todayStr());
			this.plannedSlots = planned;
			this.workPeriods = periods;
			this.clockAnomalies = anomalies;
			const ids = employees.filter(e => e.is_active !== false).map(e => e.id);
			for (const p of periods) if (!ids.includes(p.id_employee)) ids.push(p.id_employee);
			const contracts = new Map<number, number | null>(employees.map(e =>
				[e.id, e.contract_hours_week != null ? Math.round(e.contract_hours_week * 60) : null]));

			// Journées passées sans clôture : le planning fait foi sans qu'un humain l'ait dit.
			// La liste alimente la colonne « À clore » — vide par construction en mode pointeuse.
			const unclosed = unclosedDays(ids, planned, validations, this.from, this.to, todayStr(), this.source);

			// Le mode de décompte décide de ce qui fait foi : sans badgeuse, le planning EST le
			// réalisé, et l'écart ne se creuse que si quelqu'un l'a explicitement déclaré.
			// Le mode de décompte décide de ce qui fait foi ; l'heure courante (métier) borne le
			// réalisé au temps ÉCOULÉ — une semaine à venir n'a rien réalisé.
			const rows = buildTimesheet(ids, contracts, periods, anomalies, planned, todayStr(), validations, this.source,
				wallMinutesOf(new Date()) ?? 24 * 60)
				.map(r => ({
					...r,
					name: hrEmployeeNameOf(employees.find(e => e.id === r.id_employee), users),
					absence_days: this.absenceDaysInRange(absences, r.id_employee),
					unclosed: unclosed.filter(u => u.id_employee === r.id_employee),
				}))
				// `unclosed` compte comme signal d'affichage : une déclaration posée sur un jour non
				// planifié (rien de travaillé, rien de prévu) doit garder sa ligne visible — c'est
				// justement une journée qui attend le responsable.
				.filter(r => r.worked_min > 0 || r.planned_min > 0 || r.anomalies.length > 0 || r.absence_days > 0
					|| r.unclosed.length > 0 || r.open_now)
				.sort((a, b) => a.name.localeCompare(b.name));
			this.dataSource = new MatTableDataSource(rows);
		});
	}

	// Jours d'absence approuvés bornés à la période affichée.
	private absenceDaysInRange(absences: HrAbsence[], empId: number): number {
		return absences
			.filter(a => a.id_employee === empId)
			.reduce((sum, a) => sum + HrAbsence.daysInclusive(
				a.datestart > this.from ? a.datestart : this.from,
				a.dateend < this.to ? a.dateend : this.to), 0);
	}

	h(min: number): string {
		return hoursLabel(min);
	}

	deltaLabel(row: TimesheetVM): string {
		return deltaLabel(row.delta_min, '=');		// colonne dense : « = » se lit mieux qu'une case vide
	}

	// D'où sort le temps retenu, et surtout jusqu'où il court : le réalisé s'arrête à l'heure
	// qu'il est. Les pointages éventuels restent mentionnés — informatifs, ils ne pilotent rien
	// en mode planning.
	workedTooltip(row: TimesheetVM): string {
		const parts: string[] = [];
		if (this.source === 'planning') {
			parts.push(row.delta_min === 0 ? 'Le planning fait foi' : 'Planning ajusté (clôture ou déclaration)');
		}
		if (row.elapsed_min < row.planned_min) {
			parts.push('Période en cours : ' + hoursLabel(row.elapsed_min) + ' écoulées sur ' + hoursLabel(row.planned_min) + ' planifiées');
		}
		if (this.source === 'planning' && row.badged_min > 0) {
			parts.push('Badgé : ' + hoursLabel(row.badged_min) + ' (indicatif)');
		}
		return parts.join('\n');
	}

	// Une période pas encore terminée : le tableau le dit au lieu de laisser croire à un déficit.
	get hasPending(): boolean {
		return this.dataSource.data.some(r => r.elapsed_min < r.planned_min);
	}

	anomaliesTooltip(row: TimesheetVM): string {
		return row.anomalies.map(a => this.fmt(a.dat) + ' : ' + a.detail).join('\n');
	}

	// Les journées à clore, datées : le survol dit QUOI arbitrer, le bouton fact_check dit COMMENT.
	unclosedTooltip(row: TimesheetVM): string {
		return row.unclosed
			.map(u => this.fmt(u.dat) + (u.declared ? ' (déclarée, à valider)' : ' (aucun arbitrage)'))
			.join('\n');
	}

	// Combien de journées de ce salarié sont déjà arbitrées sur la période.
	validatedCount(row: TimesheetVM): number {
		return this.validations.filter(v => v.id_employee === row.id_employee && v.validated).length;
	}

	// ============================================================
	// Validation journalière (L8) — prévu / badgé / RÉMUNÉRÉ
	// ============================================================

	onValidateDays(row: TimesheetVM): void {
		const data: DayValidationDialogData = {
			id_employee: row.id_employee,
			name: row.name,
			from: this.from,
			to: this.to,
			periodLabel: this.periodLabel,
			slots: this.plannedSlots,
			periods: this.workPeriods,
			validations: this.validations,
			source: this.source,
		};
		this.dialog.open(DayValidationDialogComponent, { data, width: '900px', maxWidth: '95vw' })
			.afterClosed().subscribe((changed: DayValidationRow[] | undefined) => {
				if (!changed || !changed.length) return;

				// Un event par journée modifiée ; on recharge une fois toutes les réponses reçues.
				let remaining = changed.length;
				let failed = false;
				for (const r of changed) {
					const payload = this.hrService.buildValidationPayload({
						id: r.id, id_employee: row.id_employee, dat: r.dat,
						planned_min: r.planned_min, badged_min: r.badged_min,
						paid_min: r.paid_min, break_min: r.break_min,
						absent: r.absent, validated: r.validated, comment: r.comment,
					});
					this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_DAY_VALIDATION, payload).subscribe({
						next: () => {
							if (--remaining === 0) {
								if (!failed) this.alertService.success(changed.length + ' journée(s) enregistrée(s)');
								this.load();
							}
						},
						// Refus métier (postHrEvent le convertit en erreur RxJS) comme vraie panne.
						error: (e) => {
							failed = true;
							if (--remaining === 0) this.load();
							this.alertService.error(hrErrorMessage(e));
						},
					});
				}
			});
	}

	// ============================================================
	// Corrections (admin)
	// ============================================================

	onEditClocks(row: TimesheetVM): void {
		const data: ClockEditDialogData = {
			employeeName: row.name,
			id_employee: row.id_employee,
			clocks: this.clocks.filter(c => c.id_employee === row.id_employee),
		};
		this.dialog.open(ClockEditDialogComponent, { data, width: '520px' }).afterClosed().subscribe((result: ClockEditDialogResult) => {
			if (!result) return;
			if (result.kind === 'cancel') {
				Swal.fire({
					title: 'Annuler ce pointage ?',
					text: 'Le pointage reste archivé (barré), l\'annulation est tracée.',
					icon: 'warning', showCancelButton: true, confirmButtonText: 'Annuler le pointage', cancelButtonText: 'Retour',
				}).then(r => {
					if (!r.isConfirmed) return;
					this.hrService.postHrEvent(EVENTTYP_HR_CLOCK_CANCEL, OBJECTTYP_HR_CLOCK, { id: result.id, comment: result.comment }).subscribe({
						next: () => { this.alertService.success('Pointage annulé'); this.load(); },
						error: (e) => this.alertService.error(hrErrorMessage(e)),
					});
				});
			} else if (result.kind === 'add') {
				// `comment` : le motif saisi dans le dialog — le champ le promet pour la correction
				// comme pour l'annulation, il doit donc partir avec l'INSERT (HrClock.comment).
				this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_CLOCK, {
					id: 0, id_employee: row.id_employee, ts: result.ts, io: result.io, source: HR_CLOCK_SOURCE_MANUAL,
					comment: result.comment,
				}).subscribe({
					next: () => { this.alertService.success('Pointage ajouté'); this.load(); },
					error: (e) => this.alertService.error(hrErrorMessage(e)),
				});
			}
		});
	}

	// ============================================================
	// Export CSV (paie)
	// ============================================================

	onExportCsv(): void {
		const names = new Map(this.dataSource.data.map(r => [r.id_employee, r.name]));
		const csv = buildTimesheetCsv(this.dataSource.data, names, this.from, this.to, this.source);
		const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });		// BOM : accents OK dans Excel
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = 'compteurs_' + this.from + '_' + this.to + '.csv';
		a.click();
		URL.revokeObjectURL(a.href);
	}

	// ============================================================
	// Visa des relevés signés (event 4402)
	// ============================================================

	onCountersign(): void {
		const data: CountersignDialogData = { rows: this.pendingCountersign };
		this.dialog.open(CountersignDialogComponent, { data, width: '720px', maxWidth: '95vw' })
			.afterClosed().subscribe((countersigned: boolean | undefined) => {
				if (countersigned) this.load();
			});
	}

	// ============================================================
	// Relevé individuel du temps de travail (art. L3171-2 / D3171-8)
	// ============================================================
	//
	// LE document remettable en contrôle : jour par jour, récapitulé par semaine, un salarié par
	// page avec émargement. On imprime CE QUI EST AFFICHÉ (période et lignes de l'écran), avec les
	// mêmes chiffres — l'assemblage vit dans hr-timesheet-print.ts, le rendu dans le serveur.
	onPrintLedger(): void {
		const employees = this.dataSource.data.map(r => {
			const emp = this.employees.find(e => e.id === r.id_employee);
			return {
				id: r.id_employee,
				name: r.name,
				contract_min: emp?.contract_hours_week != null ? Math.round(emp.contract_hours_week * 60) : null,
				// Cadre dirigeant, TNS, forfait jours : pas de relevé d'HEURES — le document leur en
				// inventerait un (même résolution que les compteurs, hr-worktime.ts).
				counts_hours: countsHours(employeeWorkProfileOf(emp?.data).time_scheme),
			};
		});
		if (!employees.some(e => e.counts_hours)) {
			this.alertService.error('Aucun salarié décompté en heures sur la période : rien à imprimer.');
			return;
		}

		const today = todayStr();
		// Une semaine de janvier peut toucher deux années : les fériés des deux sont chargés.
		const holidays = new Map([...frenchHolidaysOf(+this.from.slice(0, 4)), ...frenchHolidaysOf(+this.to.slice(0, 4))]);
		const storeNames = Array.from(new Set(this.stores.map(s => s.name).filter(Boolean)));
		const doc = buildWorktimeLedgerDoc({
			storeName: storeNames.length === 1 ? storeNames[0]! : 'Tous les magasins',
			printedAt: today.slice(8, 10) + '/' + today.slice(5, 7) + '/' + today.slice(0, 4),
			periodLabel: this.periodLabel,
			from: this.from, to: this.to,
			today,
			now_min: wallMinutesOf(new Date()) ?? 24 * 60,
			source: this.source,
			employees,
			slots: this.plannedSlots,
			periods: this.workPeriods,
			anomalies: this.clockAnomalies,
			validations: this.validations,
			absenceNames: this.absenceNames,
			holidays,
		});
		// Rendu par le SERVEUR (POST /hr/print) : la fenêtre s'ouvre dans le prolongement du clic.
		this.printService.print(doc).subscribe({
			error: (e) => this.alertService.error(hrPrintErrorMessage(e)),
		});
	}
}
